<template>
    <CompassDialog
        :visible.sync="showModal"
        size="small"
        class="adyenPopup"
        :close-on-click-overlay="false"
        @closed="$emit('close')"
    >
        <template slot="header">
            <div class="compass-dialog__header">
                <div class="compass-dialog__header--title">
                    <div>
                        <SaasIcon
                            class="compass-dialog__header--title-icon mb-3 adyenIcon"
                            icon="adyen"
                            width="56"
                            height="56"
                        />
                    </div>
                    <div class="mb-1 typographies-heading-h2">Adyen</div>
                    <div class="typographies-paragraph-medium-regular">
                        {{ $l10n.__('Connectez votre compte Adyen sur Sellsy.') }}
                    </div>
                    <el-divider class="mb-0 gray-05"></el-divider>
                </div>
                <div class="compass-dialog__header--close">
                    <CompassIconButton icon="close" @click="$emit('close')" />
                </div>
            </div>
        </template>
        <template slot="default">
            <div class="dialog__default">
                <Preloader v-if="loading" :background="false" :display="loading" />
                <el-form
                    v-else
                    ref="adyenForm"
                    :model="adyenForm"
                    :rules="rules"
                    class="adyenPopup__content"
                >
                    <!-------- Error Div -------->
                    <CompassAlert
                        v-if="errorOnAdyen"
                        type="error"
                        :showClose="false"
                        :description="
                            $l10n.__(
                                'Adyen est temporairement indisponible, merci de réessayer ultérieurement.',
                            )
                        "
                    />

                    <!-------- Installation -------->
                    <div class="mb-3">
                        <div class="mb-1 typographies-paragraph-large-semibold">
                            {{ $l10n.__('Installation') }}
                        </div>
                        <div class="mb-1 typographies-paragraph-medium-regular">
                            {{
                                $l10n.__(
                                    'Connectez-vous à votre compte Adyen pour récupérer les informations nécessaires à la configuration et entrez les ci-dessous.',
                                )
                            }}
                        </div>
                        <CompassLink
                            :label="$t.__('settings.adyen.AdyenPopup.view_documentation')"
                            link="https://help.sellsy.com/fr/articles/5876306-paiement-en-ligne-des-factures-avec-adyen"
                            target="_blank"
                        >
                            <template slot="prefix">
                                <div class="typographies-heading-h4 adyen-popup__prefix-link">
                                    {{ $l10n.__("Besoin d'aide ?") }}
                                </div>
                            </template>
                        </CompassLink>
                    </div>

                    <!-- Clé API -->

                    <el-form-item :error="errors.get('api_key')" prop="api_key">
                        <div class="mb-1 flex-row typographies-paragraph-medium-semibold">
                            {{ $l10n.__('Clé API') }}
                            <span
                                class="typographies-paragraph-medium-semibold adyen-popup__required-form"
                                >*</span
                            >
                        </div>
                        <el-input v-model="adyenForm.api_key"></el-input>
                    </el-form-item>

                    <!-- Clé HMAC -->
                    <el-form-item
                        :error="errors.get('notification_hmac_key')"
                        prop="notification_hmac_key"
                    >
                        <div class="mb-1 flex-row typographies-paragraph-medium-semibold">
                            {{ $l10n.__('Clé HMAC') }}
                            <span
                                class="typographies-paragraph-medium-semibold adyen-popup__required-form"
                                >*</span
                            >
                        </div>
                        <el-input
                            v-model="adyenForm.notification_hmac_key"
                            autosize
                            resize="none"
                            type="textarea"
                        />
                    </el-form-item>

                    <!-- Environnement -->
                    <el-form-item :error="errors.get('envSelected')" prop="envSelected">
                        <div class="mb-1 flex-row typographies-paragraph-medium-semibold">
                            {{ $l10n.__('Environnement') }}
                            <span
                                class="typographies-paragraph-medium-semibold adyen-popup__required-form"
                                >*</span
                            >
                        </div>
                        <el-select
                            v-model="adyenForm.test_env"
                            class="wp-10"
                            popper-class="adyenSelectDropdown"
                        >
                            <el-option
                                v-for="(option, index) in envOptions"
                                :key="index"
                                :label="option.label"
                                :value="option.value"
                            />
                        </el-select>
                    </el-form-item>

                    <!-- Préfixe de l'URL d'API de prod -->
                    <el-form-item
                        v-if="!adyenForm.test_env"
                        :error="errors.get('live_url_prefix')"
                        prop="live_url_prefix"
                    >
                        <div class="mb-1 flex-row typographies-paragraph-medium-semibold">
                            {{ $l10n.__('API URL prefix') }}
                            <span
                                class="typographies-paragraph-medium-semibold adyen-popup__required-form"
                                >*</span
                            >
                        </div>
                        <el-input v-model="adyenForm.live_url_prefix"></el-input>
                    </el-form-item>

                    <!-- Identifiant du compte marchand -->
                    <el-form-item :error="errors.get('merchant_account')" prop="merchant_account">
                        <div class="mb-1 flex-row typographies-paragraph-medium-semibold">
                            {{ $l10n.__('Identifiant du compte marchand') }}
                            <span
                                class="typographies-paragraph-medium-semibold adyen-popup__required-form"
                                >*</span
                            >
                        </div>
                        <el-input v-model="adyenForm.merchant_account" />
                    </el-form-item>

                    <!-- Identifiant du thème -->
                    <el-form-item :error="errors.get('theme_id')" prop="theme_id">
                        <div class="mb-1 typographies-paragraph-medium-semibold">
                            {{ $l10n.__('Identifiant du thème') }}
                        </div>
                        <el-input v-model="adyenForm.theme_id" />
                    </el-form-item>

                    <!-- Moyen de paiement associé à Adyen -->
                    <el-form-item
                        :error="errors.get('payment_method_id')"
                        class="mb-2"
                        prop="payment_method_id"
                    >
                        <div class="mb-1 typographies-paragraph-medium-semibold">
                            {{ $l10n.__('Moyen de paiement associé à Adyen') }}
                            <span
                                class="typographies-paragraph-medium-semibold adyen-popup__required-form"
                                >*</span
                            >
                        </div>
                        <el-select
                            v-model="adyenForm.payment_method_id"
                            class="mb-1 wp-10"
                            popper-class="adyenSelectDropdown"
                        >
                            <el-option
                                v-for="option in meansOfPayementOptions"
                                :key="option.id"
                                :label="option.label"
                                :value="option.id"
                            />
                        </el-select>
                        <div class="typographies-paragraph-small-regular adyen-popup__caption-form">
                            {{
                                $l10n.__(
                                    'Libellé affiché dans Sellsy pour tous les paiements effectués via Adyen',
                                )
                            }}
                        </div>
                    </el-form-item>
                </el-form>
            </div>
        </template>
        <template slot="footer">
            <div class="dialog__footer">
                <CompassButton
                    :label="$t.__('common.actions.cancel')"
                    type="secondary"
                    @click="$emit('close')"
                />
                <CompassButton
                    :label="$t.__('common.actions.save')"
                    type="primary"
                    :loading="isFormSubmitted"
                    class="ml-3"
                    @click="submitForm('adyenForm')"
                />
            </div>
        </template>
    </CompassDialog>
</template>

<script>
import SaasIcon from 'vcomponents/common/SaasIcon.vue';
import { checkNotBlank } from 'vcomponents/forms/rules/Rules';
import AdyenService from 'api/plugins/adyenService.js';
import Payments from 'api/payments.js';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import Errors from 'Core/commons/Errors.js';

export default {
    components: {
        Preloader,
        SaasIcon,
    },
    data() {
        return {
            showModal: true,
            loading: true,
            isFormSubmitted: false,
            adyenForm: {},
            rules: {
                api_key: [
                    {
                        required: true,
                        message: this.$l10n.__('Ce champ est obligatoire'),
                        trigger: 'blur',
                    },
                    { validator: checkNotBlank, trigger: 'submit' },
                ],
                notification_hmac_key: [
                    {
                        required: true,
                        message: this.$l10n.__('Ce champ est obligatoire'),
                        trigger: 'blur',
                    },
                    { validator: checkNotBlank, trigger: 'submit' },
                ],
                merchant_account: [
                    {
                        required: true,
                        message: this.$l10n.__('Ce champ est obligatoire'),
                        trigger: 'blur',
                    },
                    { validator: checkNotBlank, trigger: 'submit' },
                ],
            },
            envOptions: [
                {
                    value: false,
                    label: 'Production',
                },
                {
                    value: true,
                    label: 'Test',
                },
            ],
            meansOfPayementOptions: null,
            errorOnAdyen: false,
            shouldUpdateSettings: true,
            errors: new Errors(),
        };
    },
    async created() {
        try {
            this.meansOfPayementOptions = await Payments.getAllPayments();
        } catch (error) {
            Push.error(
                this.$l10n.__('Une erreur est survenue lors de la récupération des préférences'),
            );
            this.$emit('close');
        }
        await AdyenService.getSettings()
            .then((response) => {
                this.adyenForm = response.data;
                this.adyenForm.payment_method_id =
                    this.adyenForm.payment_method_id === 0
                        ? this.meansOfPayementOptions.id
                        : this.adyenForm.payment_method_id;
            })
            .catch((error) => {
                if (error.response.data.error.code === 404) {
                    this.shouldUpdateSettings = false;
                    this.adyenForm = {
                        test_env: false,
                        payment_method_id: this.meansOfPayementOptions[0].id,
                    };
                } else {
                    Push.error(
                        this.$l10n.__(
                            'Une erreur est survenue lors de la récupération des préférences',
                        ),
                    );
                    this.$emit('close');
                }
            })
            .finally(() => (this.loading = false));
    },
    methods: {
        submitForm(formName) {
            this.errors.clearAll();
            this.$refs[formName].validate((valid) => {
                if (valid) {
                    this.isFormSubmitted = true;
                    if (this.adyenForm.test_env) {
                        this.adyenForm.live_url_prefix = null;
                    }
                    AdyenService.saveSettings(this.shouldUpdateSettings, this.adyenForm)
                        .then((response) => {
                            this.$emit('close');
                            Push.success(this.$l10n.__('Modifications correctement enregistrées'));
                        })
                        .catch((error) => {
                            this.onResponseErrorValidator(error);
                        })
                        .finally(() => (this.isFormSubmitted = false));
                }
            });
        },
        onResponseErrorValidator(error) {
            if (error.details) {
                const errorsKeys = Object.keys(error.details);
                for (const key of errorsKeys) {
                    this.errorOnAdyen = key === 'more';
                    this.errors.setOneError(key, error.details[key]);
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';

.adyenPopup {
    .compass-dialog {
        &__header {
            padding: 24px 32px;

            &--title {
                display: flex;
                flex-direction: column;
            }

            &--close {
                position: absolute;
                top: var(--spacing-3);
                right: var(--spacing-3);
            }
        }

        &__default {
            padding-bottom: 24px;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            .adyenPopup__content {
                overflow-x: hidden;
                p {
                    margin: 0;
                }
            }
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 32px 24px 32px;
        }
    }
    .adyen-popup__prefix-link,
    .adyen-popup__required-form,
    .adyen-popup__caption-form {
        color: var(--colors-common-text-standard-caption);
    }
    .adyen-popup__required-form {
        margin-left: var(--spacing-0);
    }
}

.adyenSelectDropdown.el-select-dropdown.el-popper {
    margin-top: 4px;

    div.el-scrollbar {
        div.el-select-dropdown__wrap.el-scrollbar__wrap > ul {
            padding: 10px 8px;

            .el-select-dropdown__item:hover {
                border-radius: 4px;
            }

            .el-select-dropdown__item {
                padding: 0 8px;
            }
        }
    }

    div.popper__arrow {
        display: none;
    }
}
</style>
